import React, { useState, useEffect, useRef } from 'react';
import { Country } from '../types';
import { searchCountries } from '../services/search';
import { getGameData } from '../services/dataLoader';
import { Button } from './ui/button';

interface Props {
  onSelect: (country: Country) => void;
  disabled: boolean;
  usedIsoCodes: Set<string>;
  burnedIsoCodes: Set<string>;
  allowUsed?: boolean;
}

export const CountryInput: React.FC<Props> = ({
  onSelect,
  disabled,
  usedIsoCodes,
  burnedIsoCodes,
  allowUsed = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Country[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (query.trim().length > 1) {
      const hits = searchCountries(query, getGameData().countries);
      setResults(hits);
      setSelectedIndex(0);
      setIsOpen(hits.length > 0);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [query]);

  const handleSubmit = (country: Country) => {
    // Prevent submission if used or burned
    if (burnedIsoCodes.has(country.iso3) || (usedIsoCodes.has(country.iso3) && !allowUsed)) {
      return;
    }
    
    onSelect(country);
    setQuery('');
    setIsOpen(false);
    // Keep focus on input for next question speed
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && results.length > 0) {
        handleSubmit(results[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative w-full max-w-sm">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder="Type a country name..."
        className="flex h-12 w-full rounded-md border border-input bg-transparent px-3 py-1 text-lg shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
        autoComplete="off"
        autoFocus
      />
      
      {isOpen && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md">
          {results.map((country, index) => {
            const isUsed = usedIsoCodes.has(country.iso3);
            const isBurned = burnedIsoCodes.has(country.iso3);
            const isRecoverable = allowUsed && isUsed && !isBurned;
            const isDisabled = isBurned || (!allowUsed && isUsed);

            return (
              <li
                key={country.iso3}
                className={`relative flex w-full select-none items-center rounded-sm py-2 pl-2 pr-2 text-sm outline-none transition-colors 
                  ${isDisabled ? 'opacity-50 cursor-not-allowed grayscale' : 'cursor-pointer'}
                  ${isRecoverable ? 'bg-amber-500/10' : ''}
                  ${index === selectedIndex ? 'bg-accent text-accent-foreground' : ''}
                `}
                onClick={() => !isDisabled && handleSubmit(country)}
              >
                <img 
                  src={country.flagUrl} 
                  alt={`${country.name} flag`}
                  className="mr-3 h-5 w-7 rounded-sm object-cover shadow-sm"
                />
                <span className="flex-1 font-medium truncate pr-2">{country.name}</span>
                
                {(isDisabled || isRecoverable) && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5 rounded ml-2">
                    {isBurned ? 'Burned' : isRecoverable ? 'Recover' : 'Used'}
                  </span>
                )}
                
                {!isDisabled && index === selectedIndex && (
                  <span className="text-xs text-muted-foreground ml-2">↵</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
